/**
 * plantillaIncidencias.js
 *
 * Todo el conocimiento del layout del Excel de incidencias vive aquí: construir la
 * plantilla y volver a leerla. Módulo puro (sin React ni axios), igual que
 * parseoListaRaya.js — la pantalla sólo orquesta y pinta.
 *
 * Formato: matriz empleados × días del periodo, la misma forma que el grid de
 * "Días e Incidencias". Las columnas de día son POSICIONALES: la primera es
 * diaPeriodo 1. Por eso el archivo se rechaza si el número de columnas no coincide
 * con los días del periodo — una plantilla de otra semana cargaría todo corrido.
 *
 * La plantilla se puede bajar plana o AGRUPADA POR DEPARTAMENTO, que es el mismo archivo con los
 * empleados ordenados por área y un renglón-banner antes de cada bloque. Se eligió el banner y no
 * una pestaña por departamento justamente porque no toca el formato: la ruta de importación es la
 * misma. El banner deja la columna Numero vacía, que es la marca por la que el lector lo salta.
 *
 * Reglas que no son de formato sino de negocio, y por las que este archivo clasifica
 * en tres cubetas en vez de dos:
 *
 *   - VACACIONES: no se pueden escribir como incidencia. El motor de cálculo es el
 *     dueño de esas entradas (las deriva de los MovimientoVacaciones del periodo y
 *     borra las que no respalde ningún movimiento). Aquí sólo se juntan los días y
 *     se agrupan en rangos consecutivos; el backend crea un movimiento por rango.
 *   - INCAPACIDADES: mismo problema, pero sin módulo que se pueda alimentar con lo
 *     que trae el Excel (falta folio, ramo de seguro, tipo de riesgo). Se rechazan
 *     con el motivo, para que no se importen y desaparezcan al calcular.
 */

import * as XLSX from 'xlsx-js-style'
import dayjs from 'dayjs'

import { ordenarLineas } from '../ordenLineas'

const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

const SIN_DEPARTAMENTO = 'SIN DEPARTAMENTO'

// Claves de incapacidad del catálogo de ley — misma lista que reconoce el bloque de
// sincronización de incapacidades del motor de cálculo.
const CLAVES_INCAPACIDAD = ['ATRB', 'ATRY', 'ENFG', 'INC', 'MAT', 'IMST']

const COL_NUMERO = 0
const COL_EMPLEADO = 1
const COL_PRIMER_DIA = 2

// ─── Helpers ─────────────────────────────────────────────────────────────────

const norm = (v) =>
  String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()

const esVacio = (v) => String(v ?? '').trim() === ''

/** Los días del periodo, y la fecha de cada diaPeriodo (1-based). */
export function diasDelPeriodo(prenomina) {
  const { fechaInicio, fechaFin } = prenomina || {}
  if (!fechaInicio || !fechaFin) return 0
  const n = dayjs(fechaFin).diff(dayjs(fechaInicio), 'day') + 1
  return n > 0 ? n : 0
}

export function fechaDeDiaPeriodo(prenomina, diaPeriodo) {
  if (!prenomina?.fechaInicio) return null
  return dayjs(prenomina.fechaInicio).add(diaPeriodo - 1, 'day')
}

/** La cadena de resolución del tipo de vacaciones, igual que la del motor de cálculo. */
export function resolverTipoVacaciones(tiposIncidencias = []) {
  return tiposIncidencias.find((t) => t.esVacaciones)
    || tiposIncidencias.find((t) => t.seConsidera === 4)
    || tiposIncidencias.find((t) => norm(t.clave) === 'VAC')
    || null
}

export function esTipoIncapacidad(tipo) {
  return !!tipo && (tipo.seConsidera === 3 || CLAVES_INCAPACIDAD.includes(norm(tipo.clave)))
}

/**
 * El catálogo que llega a la pantalla trae también los tipos deshabilitados (la
 * consulta no filtra por estado). La importación sólo trabaja con los activos, que
 * es lo mismo que hace el backend — si no, la plantilla ofrecería claves que el
 * servidor luego rechaza.
 */
export function tiposActivos(tiposIncidencias = []) {
  return tiposIncidencias.filter((t) => t.estado === undefined || t.estado === 1)
}

const UNIDAD_LABEL = { 1: 'Destajos', 2: 'Días', 3: 'Horas' }

// ─── Estilos ─────────────────────────────────────────────────────────────────

const BORDE = {
  top: { style: 'thin', color: { rgb: 'D9D9D9' } },
  bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
  left: { style: 'thin', color: { rgb: 'D9D9D9' } },
  right: { style: 'thin', color: { rgb: 'D9D9D9' } },
}

const S_TITULO = {
  fill: { fgColor: { rgb: '00BFD6' } },
  font: { bold: true, sz: 14, name: 'Arial', color: { rgb: 'FFFFFF' } },
  alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
}
const S_SUBTITULO = {
  font: { bold: true, sz: 11, name: 'Arial', color: { rgb: '333333' } },
  alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
}
const S_AYUDA = {
  font: { italic: true, sz: 9, name: 'Arial', color: { rgb: '888888' } },
  alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
}
const S_ENCABEZADO = {
  fill: { fgColor: { rgb: '00BFD6' } },
  font: { bold: true, sz: 10, name: 'Arial', color: { rgb: 'FFFFFF' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: BORDE,
}
const S_ENCABEZADO_FIN_SEMANA = {
  ...S_ENCABEZADO,
  fill: { fgColor: { rgb: 'FAAD14' } },
}
const S_SUBENCABEZADO = {
  fill: { fgColor: { rgb: 'E6FEFF' } },
  font: { sz: 9, name: 'Arial', color: { rgb: '666666' } },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: BORDE,
}
const S_SUBENCABEZADO_FIN_SEMANA = {
  ...S_SUBENCABEZADO,
  fill: { fgColor: { rgb: 'FFF7E6' } },
  font: { sz: 9, name: 'Arial', bold: true, color: { rgb: 'AD6800' } },
}
const S_REFERENCIA = {
  fill: { fgColor: { rgb: 'F5F5F5' } },
  font: { sz: 10, name: 'Arial', color: { rgb: '595959' } },
  alignment: { horizontal: 'left', vertical: 'center' },
  border: BORDE,
}
const S_CELDA = {
  font: { sz: 10, name: 'Arial', bold: true, color: { rgb: '1677FF' } },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: BORDE,
}
const S_CELDA_FIN_SEMANA = {
  ...S_CELDA,
  fill: { fgColor: { rgb: 'FFF7E6' } },
}
// Banda de departamento: la misma de la Lista de Raya, para que los dos Excel de la misma
// nómina se lean igual.
const S_BANNER = {
  fill: { fgColor: { rgb: 'DFF5F9' } },
  font: { bold: true, sz: 10, name: 'Arial', color: { rgb: '005F6B' } },
  alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
  border: {
    top: { style: 'thin', color: { rgb: '00BFD6' } },
    bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
  },
}

// ─── Construcción de la plantilla ────────────────────────────────────────────

/** Escribe (o crea) la celda en `ref` con valor de texto y estilo. */
function celda(ws, r, c, valor, estilo) {
  const ref = XLSX.utils.encode_cell({ r, c })
  ws[ref] = { t: 's', v: valor == null ? '' : String(valor), s: estilo }
}

/**
 * La etiqueta del banner de departamento, que además es la clave con la que se agrupa: dos
 * departamentos con el mismo número y nombre son el mismo bloque, y si difieren en cualquiera de
 * los dos la etiqueta ya sale distinta.
 */
function etiquetaDepartamento(linea) {
  const nombre = String(linea?.departamentoNombre || '').trim()
  if (!nombre) return SIN_DEPARTAMENTO
  const numero = linea?.departamentoNumero
  return numero === null || numero === undefined
    ? nombre.toUpperCase()
    : `${numero}  ${nombre.toUpperCase()}`
}

function hojaIncidencias(prenomina, { porDepartamento = false } = {}) {
  const nDias = diasDelPeriodo(prenomina)
  // El agrupado no inventa comparador: usa el motor de orden que comparten la tabla y el visor de
  // recibos. COMPARADORES.departamento encadena departamento → código → nombre y manda los vacíos
  // al final, así que SIN DEPARTAMENTO cae solo al último bloque.
  const lineas = porDepartamento
    ? ordenarLineas(prenomina?.lineas || [], 'departamento', false)
    : (prenomina?.lineas || [])
  const ultimaCol = COL_PRIMER_DIA + Math.max(nDias, 1) - 1

  const ws = {}

  // Mismo texto de encabezado que la Lista de Raya, para que los dos archivos de la
  // misma nómina se identifiquen igual.
  const periodoTexto = [
    prenomina?.periodoNumero != null ? `Periodo ${prenomina.periodoNumero}` : 'Periodo',
    prenomina?.tipoPeriodoNombre || '',
    prenomina?.fechaInicio && prenomina?.fechaFin
      ? `· del ${dayjs(prenomina.fechaInicio).format('DD/MM/YYYY')} al ${dayjs(prenomina.fechaFin).format('DD/MM/YYYY')}`
      : '',
  ].filter(Boolean).join(' ')

  // r0 título · r1 periodo · r2 ayuda · r3 espaciador
  celda(ws, 0, 0, 'INCIDENCIAS DE NÓMINA', S_TITULO)
  celda(ws, 1, 0, periodoTexto, S_SUBTITULO)
  celda(ws, 2, 0,
    'No muevas ni agregues columnas de día y no cambies la columna Numero: la posición de cada columna es la que manda.',
    S_AYUDA)
  for (let c = 1; c <= ultimaCol; c++) {
    celda(ws, 0, c, '', S_TITULO)
    celda(ws, 1, c, '', {})
    celda(ws, 2, c, '', {})
  }
  celda(ws, 3, 0, '', {})

  // r4 encabezado · r5 día de la semana
  celda(ws, 4, COL_NUMERO, 'Numero', S_ENCABEZADO)
  celda(ws, 4, COL_EMPLEADO, 'Empleado', S_ENCABEZADO)
  celda(ws, 5, COL_NUMERO, '', S_SUBENCABEZADO)
  celda(ws, 5, COL_EMPLEADO, '', S_SUBENCABEZADO)

  const finDeSemana = []
  for (let d = 1; d <= nDias; d++) {
    const fecha = fechaDeDiaPeriodo(prenomina, d)
    const esFin = !!fecha && (fecha.day() === 0 || fecha.day() === 6)
    finDeSemana.push(esFin)
    const col = COL_PRIMER_DIA + d - 1
    celda(ws, 4, col, fecha ? fecha.format('DD') : String(d), esFin ? S_ENCABEZADO_FIN_SEMANA : S_ENCABEZADO)
    celda(ws, 5, col, fecha ? DIAS_ES[fecha.day()] : '', esFin ? S_SUBENCABEZADO_FIN_SEMANA : S_SUBENCABEZADO)
  }

  // Renglones de empleado, ya prellenados, con un banner por departamento cuando se pidió agrupado.
  const PRIMER_DATO = 6
  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: ultimaCol } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: ultimaCol } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: ultimaCol } },
  ]
  const alturas = [{ hpt: 26 }, { hpt: 18 }, { hpt: 15 }, { hpt: 6 }, { hpt: 20 }, { hpt: 15 }]

  let r = PRIMER_DATO
  let departamentoActual = null

  lineas.forEach((linea) => {
    if (porDepartamento) {
      const etiqueta = etiquetaDepartamento(linea)
      if (etiqueta !== departamentoActual) {
        departamentoActual = etiqueta
        // La columna Numero del banner va VACÍA a propósito: es lo que hace que
        // analizarHojaIncidencias lo salte sin ruido en vez de buscar un empleado que no existe.
        // Y por eso mismo el merge arranca en COL_EMPLEADO y no en 0: en xlsx un rango combinado
        // muestra el valor de su celda ancla, así que anclarlo en la columna vacía escondería el
        // nombre del departamento. La celda 0 se pinta con la misma banda para que se vea continua.
        celda(ws, r, COL_NUMERO, '', S_BANNER)
        celda(ws, r, COL_EMPLEADO, etiqueta, S_BANNER)
        for (let c = COL_PRIMER_DIA; c <= ultimaCol; c++) celda(ws, r, c, '', S_BANNER)
        merges.push({ s: { r, c: COL_EMPLEADO }, e: { r, c: ultimaCol } })
        alturas[r] = { hpt: 18 }
        r++
      }
    }

    const numero = linea.serieNumeroEmpleado
      || (linea.codigo != null && linea.codigo !== '' ? String(linea.codigo) : '')
    const nombre = [linea.nombre, linea.apellidoPaterno, linea.apellidoMaterno].filter(Boolean).join(' ')

    celda(ws, r, COL_NUMERO, numero, { ...S_REFERENCIA, alignment: { horizontal: 'center', vertical: 'center' } })
    celda(ws, r, COL_EMPLEADO, nombre, S_REFERENCIA)
    for (let d = 1; d <= nDias; d++) {
      celda(ws, r, COL_PRIMER_DIA + d - 1, '', finDeSemana[d - 1] ? S_CELDA_FIN_SEMANA : S_CELDA)
    }
    r++
  })

  // `r` quedó en el renglón siguiente al último escrito; sin líneas se deja el encabezado solo.
  const ultimaFila = Math.max(r - 1, PRIMER_DATO)
  ws['!ref'] = XLSX.utils.encode_range(
    { s: { r: 0, c: 0 }, e: { r: ultimaFila, c: ultimaCol } }
  )
  ws['!merges'] = merges
  // Los huecos que deja `alturas` (los renglones de empleado) quedan como undefined, que es lo
  // mismo que no fijarles altura.
  ws['!rows'] = alturas
  ws['!cols'] = [{ wch: 12 }, { wch: 34 }, ...Array.from({ length: Math.max(nDias, 1) }, () => ({ wch: 8 }))]
  // Ojo: !freeze / !panes no hacen nada en xlsx-js-style, no vale la pena ponerlos.

  return ws
}

function hojaCatalogo(tiposIncidencias, tipoVacaciones) {
  const filas = [['Clave', 'Nombre', 'Se captura como', 'Nota']]

  const meta = []
  for (const t of tiposIncidencias) {
    const esVac = tipoVacaciones && String(t._id) === String(tipoVacaciones._id)
    const esIncap = esTipoIncapacidad(t)
    filas.push([
      t.clave || '',
      t.nombre || '',
      esVac ? 'Vacaciones' : esIncap ? 'No se importa' : (UNIDAD_LABEL[t.tipo] || 'Días'),
      esVac
        ? 'Se registra como movimiento de vacaciones. Marca VAC día por día; los días seguidos se agrupan en un solo movimiento.'
        : esIncap
          ? 'No se puede importar desde aquí: captúrala en la pestaña Incapacidades del sobre-recibo.'
          : t.tipo === 3
            ? 'Escribe la cantidad de horas con dos puntos, por ejemplo TE:2.5'
            : 'Escribe sólo la clave; sin cantidad se toma 1.',
    ])
    meta.push({ esVac, esIncap })
  }

  const ws = XLSX.utils.aoa_to_sheet(filas)
  ws['!cols'] = [{ wch: 12 }, { wch: 34 }, { wch: 18 }, { wch: 80 }]

  for (let c = 0; c < 4; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c })
    if (ws[ref]) ws[ref].s = S_ENCABEZADO
  }
  meta.forEach((m, i) => {
    const estilo = {
      font: {
        sz: 10,
        name: 'Arial',
        color: { rgb: m.esIncap ? 'CF1322' : m.esVac ? '531DAB' : '333333' },
      },
      alignment: { vertical: 'center', wrapText: true },
      border: BORDE,
      ...(m.esIncap ? { fill: { fgColor: { rgb: 'FFF1F0' } } } : {}),
      ...(m.esVac ? { fill: { fgColor: { rgb: 'F9F0FF' } } } : {}),
    }
    for (let c = 0; c < 4; c++) {
      const ref = XLSX.utils.encode_cell({ r: i + 1, c })
      if (ws[ref]) ws[ref].s = estilo
    }
  })

  return ws
}

function hojaInstrucciones(prenomina, tipoVacaciones) {
  const claveVac = tipoVacaciones?.clave || 'VAC'
  const pasos = [
    ['Cómo llenar este archivo'],
    [''],
    ['1. Un renglón por empleado. La columna Numero y la columna Empleado ya vienen llenas: no las cambies ni cambies el orden de los renglones (puedes borrar los renglones de empleados que no vayas a capturar).'],
    ['2. Cada columna de día es un día del periodo, en orden. No agregues, quites ni muevas columnas: si el archivo no trae exactamente los días de esta nómina, la importación lo rechaza.'],
    ['2.1. Si descargaste la plantilla AGRUPADA POR DEPARTAMENTO, los renglones con el nombre del área son sólo separadores: la importación los ignora, así que puedes dejarlos o borrarlos. Lo único que identifica al empleado es la columna Numero.'],
    ['3. En la celda del día escribe la CLAVE de la incidencia (por ejemplo FI). Consulta la hoja "Catalogo de claves" para ver las que tiene dadas de alta tu empresa.'],
    ['4. Si la incidencia se mide en horas o destajos, escribe la cantidad después de dos puntos: TE:2.5 son 2.5 horas. Sin cantidad se toma 1.'],
    ['5. Si un mismo día lleva más de una incidencia, sepáralas con coma: RET,TE:2'],
    ['6. Una celda vacía significa "sin incidencia". Ojo: si ese día ya tenía una incidencia capturada en el sistema, dejarlo vacío en el Excel la BORRA — el archivo manda. Eso aplica al renglón de un empleado en el que hayas capturado algo; un renglón que dejes COMPLETAMENTE vacío se toma como "no lo toqué" y sus incidencias se quedan como están.'],
    [`7. VACACIONES: marca ${claveVac} en cada día que el empleado esté de vacaciones. Se paga un día por cada celda marcada, así que marca exactamente los días que le vas a pagar. No se guardan como incidencia: los días seguidos se agrupan en UN movimiento de vacaciones (con su fecha inicial y final) que se registra en el módulo de vacaciones, y el sistema los devuelve a la rejilla al calcular.`],
    ['7.1. El saldo del kárdex descuenta los días hábiles del rango (el domingo no cuenta), igual que cuando se capturan desde la pestaña de Vacaciones del sobre-recibo.'],
    ['8. El tipo de vacación (con prima o sin prima) y la fecha de pago se eligen una sola vez, en la pantalla de importación, para todo el archivo.'],
    ['9. INCAPACIDADES: no se pueden importar desde aquí. Captúralas en la pestaña Incapacidades del sobre-recibo, que es donde se registra el folio del IMSS.'],
    ['10. Después de importar, presiona "Calcular todo" en la nómina: hasta entonces los importes no se mueven.'],
    [''],
    [prenomina?.fechaInicio && prenomina?.fechaFin
      ? `Este archivo corresponde al periodo del ${dayjs(prenomina.fechaInicio).format('DD/MM/YYYY')} al ${dayjs(prenomina.fechaFin).format('DD/MM/YYYY')}.`
      : ''],
  ]

  const ws = XLSX.utils.aoa_to_sheet(pasos)
  ws['!cols'] = [{ wch: 110 }]
  if (ws['A1']) {
    ws['A1'].s = { font: { bold: true, sz: 13, name: 'Arial', color: { rgb: '00BFD6' } } }
  }
  for (let r = 2; r < pasos.length; r++) {
    const ref = XLSX.utils.encode_cell({ r, c: 0 })
    if (ws[ref]) {
      ws[ref].s = { font: { sz: 10, name: 'Arial' }, alignment: { wrapText: true, vertical: 'top' } }
    }
  }
  return ws
}

/**
 * Workbook completo de la plantilla, listo para XLSX.writeFile.
 *
 * `porDepartamento` sólo cambia el ORDEN de los renglones y mete un banner por área: el formato
 * que lee la importación es idéntico, un solo archivo y una sola carga.
 */
export function construirPlantillaIncidencias({ prenomina, tiposIncidencias = [], porDepartamento = false }) {
  const activos = tiposActivos(tiposIncidencias)
  const tipoVacaciones = resolverTipoVacaciones(activos)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, hojaIncidencias(prenomina, { porDepartamento }), 'Incidencias')
  XLSX.utils.book_append_sheet(wb, hojaCatalogo(activos, tipoVacaciones), 'Catalogo de claves')
  XLSX.utils.book_append_sheet(wb, hojaInstrucciones(prenomina, tipoVacaciones), 'Instrucciones')
  return wb
}

export function nombreArchivoPlantilla(prenomina, { porDepartamento = false } = {}) {
  const etiqueta = String(prenomina?.nombre || 'nomina').replace(/[/\\?*[\]:#\s]/g, '_')
  return `incidencias_${etiqueta}${porDepartamento ? '_por_departamento' : ''}.xlsx`
}

// ─── Lectura del archivo ─────────────────────────────────────────────────────

/**
 * Días hábiles de un rango, contando todo menos el domingo. Es la misma cuenta que
 * hacen calcularDiasHabiles del formulario de vacaciones y la del batch masivo del
 * backend; aquí sólo se usa para avisar en el preview cuándo el saldo del kárdex no
 * va a coincidir con los días marcados.
 */
function contarDiasHabiles(inicio, fin) {
  if (!inicio || !fin) return 0
  let n = 0
  let cursor = inicio
  while (!cursor.isAfter(fin, 'day')) {
    if (cursor.day() !== 0) n++
    cursor = cursor.add(1, 'day')
  }
  return n
}

/** [3,4,5,9,10] → [[3,4,5],[9,10]] — cada racha se vuelve un movimiento de vacaciones. */
export function agruparConsecutivos(dias) {
  const ordenados = [...new Set(dias)].sort((a, b) => a - b)
  const rachas = []
  for (const dia of ordenados) {
    const ultima = rachas[rachas.length - 1]
    if (ultima && dia === ultima[ultima.length - 1] + 1) ultima.push(dia)
    else rachas.push([dia])
  }
  return rachas
}

/**
 * Un token de celda → { clave, cantidad }.
 * 'FI' → { clave: 'FI', cantidad: null }   ·   'TE:2.5' → { clave: 'TE', cantidad: 2.5 }
 */
function parsearToken(token) {
  const m = String(token).match(/^(.*?)\s*[:=]\s*([\d.,]+)$/)
  if (m) {
    const cantidad = parseFloat(String(m[2]).replace(',', '.'))
    return { clave: norm(m[1]), cantidad: Number.isFinite(cantidad) ? cantidad : null }
  }
  return { clave: norm(token), cantidad: null }
}

function construirIndiceTipos(tiposIncidencias) {
  const porClave = new Map()
  for (const t of tiposIncidencias) {
    const clave = norm(t.clave)
    if (clave && !porClave.has(clave)) porClave.set(clave, t)
  }
  return porClave
}

function construirIndiceLineas(lineas) {
  const porNumero = new Map()
  const porCodigo = new Map()
  for (const l of lineas) {
    const num = norm(l.serieNumeroEmpleado)
    if (num && !porNumero.has(num)) porNumero.set(num, l)
    if (l.codigo != null && l.codigo !== '') {
      const cod = norm(l.codigo)
      if (!porCodigo.has(cod)) porCodigo.set(cod, l)
      // El código se ve rellenado con ceros en pantalla; se acepta escrito de las dos formas.
      const rellenado = cod.padStart(4, '0')
      if (!porCodigo.has(rellenado)) porCodigo.set(rellenado, l)
    }
  }
  return { porNumero, porCodigo }
}

function buscarLinea(indice, texto) {
  const clave = norm(texto)
  if (!clave) return null
  return indice.porNumero.get(clave)
    || indice.porCodigo.get(clave)
    || indice.porCodigo.get(clave.padStart(4, '0'))
    || indice.porNumero.get(clave.replace(/^0+/, ''))
    || null
}

/**
 * Lee la matriz del Excel y la deja lista para el preview y para el POST.
 *
 * @param filas  filas crudas de la hoja (XLSX.utils.sheet_to_json con header:1)
 * @returns { error, filas, resumen }
 */
export function analizarHojaIncidencias({ filas = [], prenomina, tiposIncidencias = [] }) {
  const vacio = { error: null, filas: [], resumen: null }

  const nDias = diasDelPeriodo(prenomina)
  if (!nDias) return { ...vacio, error: 'La prenómina no tiene un periodo con fechas válidas' }

  const iEncabezado = filas.findIndex(
    (f) => norm(f?.[COL_NUMERO]) === 'NUMERO' && norm(f?.[COL_EMPLEADO]).startsWith('EMPLEADO')
  )
  if (iEncabezado === -1) {
    return {
      ...vacio,
      error: 'No se encontró el renglón de encabezado con las columnas "Numero" y "Empleado". Descarga la plantilla de esta pantalla y captura sobre ella.',
    }
  }

  const encabezado = filas[iEncabezado] || []
  let ultimaColumna = -1
  for (let c = encabezado.length - 1; c >= COL_PRIMER_DIA; c--) {
    if (!esVacio(encabezado[c])) { ultimaColumna = c; break }
  }
  const columnasDia = ultimaColumna === -1 ? 0 : ultimaColumna - COL_PRIMER_DIA + 1
  if (columnasDia !== nDias) {
    return {
      ...vacio,
      error: `El archivo trae ${columnasDia} columna(s) de día y este periodo tiene ${nDias}. Es la plantilla de otra nómina: descarga la de este periodo.`,
    }
  }

  const activos = tiposActivos(tiposIncidencias)
  const indiceLineas = construirIndiceLineas(prenomina?.lineas || [])
  const tiposPorClave = construirIndiceTipos(activos)
  const tipoVacaciones = resolverTipoVacaciones(activos)

  const resultado = []
  const empleadosVistos = new Set()

  for (let r = iEncabezado + 1; r < filas.length; r++) {
    const fila = filas[r] || []
    const celdasDia = Array.from({ length: nDias }, (_, i) => fila[COL_PRIMER_DIA + i])
    // El criterio mira SÓLO la columna Numero, que es la única con la que se identifica a un
    // empleado. La de Empleado es informativa, y en la plantilla agrupada por departamento la
    // ocupa el banner del área: si contara para esto, cada banner se leería como un renglón con
    // un número de empleado inexistente.
    const sinNumero = esVacio(fila[COL_NUMERO])
    const todoVacio = celdasDia.every(esVacio)

    if (sinNumero) {
      // Renglones de adorno de la propia plantilla. El inmediatamente debajo del encabezado es el
      // de los días de la semana ("Lun", "Mar", ...) — ése SÍ trae texto en las columnas de día,
      // por eso se salta por posición y no por `todoVacio`. Los banners de departamento y los
      // espaciadores caen por `todoVacio`: sin número no hay a quién aplicarle nada y, sin
      // captura, no hay nada que aplicar.
      if (r === iEncabezado + 1 || todoVacio) continue
      resultado.push({
        filaExcel: r + 1,
        numeroArchivo: '',
        nombreArchivo: '',
        errores: ['Falta el número de empleado en este renglón'],
        dias: [], diasVacaciones: [], rangosVacaciones: [], resumenIncidencias: [], reemplaza: 0,
      })
      continue
    }

    const numeroArchivo = String(fila[COL_NUMERO] ?? '').trim()
    const nombreArchivo = String(fila[COL_EMPLEADO] ?? '').trim()
    const linea = buscarLinea(indiceLineas, numeroArchivo)

    const base = {
      filaExcel: r + 1,
      numeroArchivo,
      nombreArchivo,
      dias: [],
      diasVacaciones: [],
      rangosVacaciones: [],
      resumenIncidencias: [],
      reemplaza: 0,
      errores: [],
    }

    if (!linea) {
      base.errores.push(`No hay ningún empleado con el número "${numeroArchivo}" en esta nómina`)
      resultado.push(base)
      continue
    }

    const claveEmpleado = String(linea.empleadoId)
    if (empleadosVistos.has(claveEmpleado)) {
      base.errores.push('Este empleado ya venía en un renglón anterior del archivo')
      resultado.push(base)
      continue
    }
    empleadosVistos.add(claveEmpleado)

    base.empleadoId = linea.empleadoId
    base.empleadoNumero = linea.serieNumeroEmpleado || ''
    base.empleadoNombre = [linea.nombre, linea.apellidoPaterno, linea.apellidoMaterno]
      .filter(Boolean).join(' ')
    base.bloqueada = ['timbrada', 'historica'].includes(linea.estatus)

    if (todoVacio) {
      base.vacio = true
      resultado.push(base)
      continue
    }

    const totalesPorClave = new Map()
    const clavesInvalidas = new Set()
    const clavesIncapacidad = new Set()

    for (let i = 0; i < nDias; i++) {
      const texto = String(celdasDia[i] ?? '').trim()
      const diaPeriodo = i + 1

      // Se agrega el día aunque quede sin entradas: una celda vaciada a propósito
      // limpia lo que hubiera en el sistema, y eso lo decide el backend con la lista
      // de días que le llega. Los días que el usuario nunca tocó no viajan.
      if (!texto) continue

      const entradas = []
      let tieneVacacion = false

      for (const token of texto.split(/[,;]/).map((t) => t.trim()).filter(Boolean)) {
        const { clave, cantidad } = parsearToken(token)
        if (!clave) continue

        const tipo = tiposPorClave.get(clave)
        if (!tipo) { clavesInvalidas.add(clave); continue }

        if (tipoVacaciones && String(tipo._id) === String(tipoVacaciones._id)) {
          tieneVacacion = true
          continue
        }
        if (esTipoIncapacidad(tipo)) { clavesIncapacidad.add(tipo.clave || clave); continue }

        const cant = cantidad != null && cantidad > 0 ? cantidad : 1
        entradas.push({ clave: tipo.clave, nombre: tipo.nombre || tipo.clave, cantidad: cant })
        totalesPorClave.set(tipo.clave, (totalesPorClave.get(tipo.clave) || 0) + cant)
      }

      if (tieneVacacion) base.diasVacaciones.push(diaPeriodo)
      base.dias.push({ diaPeriodo, entradas })
    }

    if (clavesInvalidas.size) {
      base.errores.push(
        `Clave${clavesInvalidas.size > 1 ? 's' : ''} que no existe${clavesInvalidas.size > 1 ? 'n' : ''} o está${clavesInvalidas.size > 1 ? 'n' : ''} deshabilitada${clavesInvalidas.size > 1 ? 's' : ''} en el catálogo: ${[...clavesInvalidas].join(', ')}`
      )
    }
    if (clavesIncapacidad.size) {
      base.errores.push(
        `${[...clavesIncapacidad].join(', ')} es una incapacidad y no se puede importar: captúrala en la pestaña Incapacidades del sobre-recibo`
      )
    }
    if (base.diasVacaciones.length && !tipoVacaciones) {
      base.errores.push('La empresa no tiene ningún tipo de incidencia de vacaciones en el catálogo')
    }

    base.resumenIncidencias = [...totalesPorClave.entries()].map(([clave, total]) => ({ clave, total }))
    base.rangosVacaciones = agruparConsecutivos(base.diasVacaciones).map((racha) => {
      const inicio = fechaDeDiaPeriodo(prenomina, racha[0])
      const fin = fechaDeDiaPeriodo(prenomina, racha[racha.length - 1])
      return {
        inicio,
        fin,
        // `dias` son las celdas marcadas, que es lo que se termina pagando (el motor
        // escribe una entrada por día del rango). `diasHabiles` es lo que descuenta el
        // kárdex, que excluye el domingo — la misma cuenta que hace el formulario de
        // vacaciones. Cuando difieren, el preview lo dice.
        dias: racha.length,
        diasHabiles: contarDiasHabiles(inicio, fin),
      }
    })

    // Cuántos de los días que trae el archivo ya tenían algo capturado en el sistema.
    const diasConDatoPrevio = new Set(
      (linea.incidencias || [])
        .filter((d) => (d.entradas || []).length > 0)
        .map((d) => d.diaPeriodo)
    )
    base.reemplaza = base.dias.filter((d) => diasConDatoPrevio.has(d.diaPeriodo)).length

    resultado.push(base)
  }

  const importables = resultado.filter(
    (f) => f.empleadoId && !f.errores.length && !f.bloqueada && !f.vacio
  )

  const resumen = {
    total: resultado.length,
    importables: importables.length,
    conError: resultado.filter((f) => f.errores.length).length,
    bloqueadas: resultado.filter((f) => f.bloqueada).length,
    sinCaptura: resultado.filter((f) => f.vacio).length,
    conVacaciones: importables.filter((f) => f.diasVacaciones.length).length,
    movimientosVacaciones: importables.reduce((s, f) => s + f.rangosVacaciones.length, 0),
    diasReemplazados: importables.reduce((s, f) => s + f.reemplaza, 0),
  }

  return { error: null, filas: resultado, resumen }
}

/** Las filas del análisis → el `filas` que espera prenomina/importarIncidenciasExcel. */
export function construirFilasPayload(filasAnalisis = []) {
  return filasAnalisis
    .filter((f) => f.empleadoId && !f.errores.length && !f.bloqueada && !f.vacio)
    .map((f) => ({
      empleadoId: f.empleadoId,
      dias: f.dias.map((d) => ({
        diaPeriodo: d.diaPeriodo,
        entradas: d.entradas.map((e) => ({ clave: e.clave, cantidad: e.cantidad })),
      })),
      diasVacaciones: f.diasVacaciones,
    }))
}
